import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, FileText, Bell, Calendar as CalIcon, ShoppingBasket, MessageCircle, ArrowRight, Clock, FolderArchive } from "lucide-react";
import { api } from "@/lib/api";
import { useSession } from "@/App";
import TrialBanner from "@/components/TrialBanner";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import OrganizationScore from "@/components/OrganizationScore";

const categoryColors = {
  ricevuta: "#F2CC8F",
  referto_medico: "#B5838D",
  garanzia: "#81B29A",
  assicurazione: "#3D5A80",
  bolletta: "#E07A5F",
  contratto: "#A8A29E",
  foto_famiglia: "#F28C71",
  documento_identita: "#3D5A80",
  scuola: "#81B29A",
  altro: "#78716C",
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch { return iso; }
};

export default function DashboardPage() {
  const { user, family, members } = useSession();
  const [stats, setStats] = useState({ documents: 0, pending_shopping: 0, upcoming_expiries: 0, upcoming_events: 0 });
  const [timeline, setTimeline] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/timeline").then(({ data }) => setTimeline(data)).catch(() => {});
    api.get("/calendar").then(({ data }) => setEvents(data)).catch(() => {});
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcomingEvents = events.filter(e => e.date >= today).slice(0, 5);

  return (
    <div className="px-5 md:px-10 py-8 max-w-6xl mx-auto">
      <TrialBanner />
      <OnboardingChecklist />
      <OrganizationScore />
      <div className="flex items-start justify-between gap-4 mb-8 animate-fade-up">
        <div>
          <p className="text-xs uppercase tracking-widest text-tertiary-fg font-semibold">{family?.name}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-2">
            Ciao {user?.name?.split(" ")[0]} <span className="text-brand">.</span>
          </h1>
          <p className="text-secondary-fg text-sm mt-1">Ecco la tua memoria familiare oggi.</p>
        </div>
        <div className="hidden sm:flex -space-x-2">
          {members?.slice(0, 4).map(m => (
            <div
              key={m.id}
              className="w-9 h-9 rounded-full grid place-items-center text-white text-xs font-semibold border-2 border-app"
              style={{ backgroundColor: m.avatar_color || "#E07A5F" }}
              title={m.name}
            >
              {m.name?.[0]?.toUpperCase()}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {[
          { label: "Documenti", value: stats.documents, icon: FolderArchive, accent: "var(--brand)", to: "/vault", testid: "stat-documents" },
          { label: "Spesa", value: stats.pending_shopping, icon: ShoppingBasket, accent: "var(--sage)", to: "/shopping", testid: "stat-shopping" },
          { label: "In scadenza", value: stats.upcoming_expiries, icon: Bell, accent: "var(--amber)", to: "/vault", testid: "stat-expiries" },
          { label: "Eventi", value: stats.upcoming_events, icon: CalIcon, accent: "var(--info)", to: "/calendar", testid: "stat-events" },
        ].map((s, i) => (
          <Link
            key={s.label}
            to={s.to}
            data-testid={s.testid}
            className="bg-surface border border-subtle rounded-2xl p-4 hover:bg-elevated transition-colors animate-fade-up"
            style={{ animationDelay: `${i * 50}ms` }}
          >
            <div className="flex items-start justify-between">
              <s.icon className="w-5 h-5" style={{ color: s.accent }} strokeWidth={1.6} />
            </div>
            <div className="mt-3 text-3xl font-display font-semibold">{s.value}</div>
            <div className="text-xs text-tertiary-fg mt-1">{s.label}</div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
        <Link
          to="/ai"
          data-testid="dashboard-ai-card"
          className="md:col-span-2 bg-surface border border-subtle rounded-3xl p-6 group hover:bg-elevated transition-colors animate-fade-up"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-2xl bg-brand grid place-items-center">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={1.6} />
            </div>
            <div>
              <div className="font-display text-lg font-medium">Chiedi alla tua AI</div>
              <div className="text-xs text-tertiary-fg">&ldquo;Quando scade l&apos;assicurazione auto?&rdquo;</div>
            </div>
          </div>
          <div className="text-sm text-secondary-fg leading-relaxed">
            Interroga la memoria di tutta la famiglia in linguaggio naturale.
          </div>
          <div className="mt-4 flex items-center gap-1 text-brand text-sm font-medium group-hover:gap-2 transition-all">
            Apri chat AI <ArrowRight className="w-4 h-4" />
          </div>
        </Link>

        <Link
          to="/chat"
          data-testid="dashboard-family-chat-card"
          className="bg-surface border border-subtle rounded-3xl p-6 hover:bg-elevated transition-colors animate-fade-up"
        >
          <div className="w-10 h-10 rounded-2xl bg-elevated grid place-items-center mb-3">
            <MessageCircle className="w-5 h-5 text-sage" strokeWidth={1.6} />
          </div>
          <div className="font-display text-lg font-medium">Chat famiglia</div>
          <div className="text-xs text-tertiary-fg mt-1">L&apos;AI ascolta e organizza</div>
          <div className="mt-4 text-sm text-secondary-fg leading-relaxed">
            Messaggi, vocali, foto e documenti.
          </div>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2 bg-surface border border-subtle rounded-3xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-medium flex items-center gap-2">
              <Clock className="w-5 h-5 text-tertiary-fg" strokeWidth={1.6} /> Timeline famiglia
            </h2>
          </div>
          {timeline.length === 0 ? (
            <div className="text-sm text-tertiary-fg py-8 text-center">
              Nessuna attività ancora. Carica un documento o aggiungi un evento.
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-auto">
              {timeline.slice(0, 12).map((item, i) => (
                <div key={`${item.type}-${item.id}`} className="flex items-start gap-3 animate-fade-up" style={{ animationDelay: `${i * 30}ms` }}>
                  <div
                    className="w-8 h-8 rounded-full grid place-items-center shrink-0 mt-0.5"
                    style={{ backgroundColor: categoryColors[item.subtitle] || "var(--bg-elevated)" }}
                  >
                    {item.type === "document" ? <FileText className="w-4 h-4 text-white" strokeWidth={1.6} /> : <CalIcon className="w-4 h-4 text-white" strokeWidth={1.6} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{item.title}</div>
                    <div className="text-xs text-tertiary-fg mt-0.5">
                      {item.subtitle} · {item.user || "—"} · {formatDate(item.date)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-surface border border-subtle rounded-3xl p-6">
          <h2 className="font-display text-xl font-medium flex items-center gap-2 mb-4">
            <CalIcon className="w-5 h-5 text-tertiary-fg" strokeWidth={1.6} /> Prossimi eventi
          </h2>
          {upcomingEvents.length === 0 ? (
            <div className="text-sm text-tertiary-fg py-8 text-center">Nessun evento.</div>
          ) : (
            <div className="space-y-3">
              {upcomingEvents.map((e, i) => (
                <div key={e.id} className="bg-elevated rounded-2xl p-3 animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                  <div className="text-xs font-mono text-brand">{formatDate(e.date)}</div>
                  <div className="text-sm font-medium mt-1">{e.title}</div>
                  {e.description && <div className="text-xs text-tertiary-fg mt-1 line-clamp-2">{e.description}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
